/**
 * MobileProbables — dedicated mobile probable-pitchers page (v3 spec).
 * Ref: Probables v3.dc.html + child ProbMatch.dc.html (center treatment B: VS + time).
 *
 * Single-day (◀ Today ▶), no team <select>, no highlight-filter. The parent
 * (MobileTeamHome) provides the shared TeamChrome nav + the flat team banner
 * (MobilePageHeader: logo / name / pts / "PROBABLE" badge); this page renders the
 * date section header + the matchup list.
 *
 * "Your matchup" = Option A ONLY: a short gold edge tick at the row's left + a small
 * gold dot before/after the owned pitcher's name. NO loud YOURS pill, NO full-height
 * rail, NO row tint, NO gold-name treatment (names are always white).
 *
 * Mobile-internal: reuses ONLY probablePitchersApi, the mobile player modal, the
 * shared MobileSectionHeader, and color tokens. Tokens only; tints via withAlpha.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { dynastyTheme, withAlpha } from '../../../services/colorService';
import { leaguesAPI } from '../../../services/apiService';
import { fetchProbablePitchers } from '../../../services/probablePitchersApi';
import useDefaultLeague from '../../../hooks/useDefaultLeague';
import { MobilePlayerModal } from '../components/player';
import MobileSectionHeader from '../components/MobileSectionHeader';

const C = dynastyTheme.tokens.colors;

// ============================================================
// Mobile-internal helpers (NOT desktop imports)
// ============================================================

// buildStatLine — W-L · ERA · WHIP · IP · K. NO FALLBACKS: null parts are omitted,
// never replaced with sentinels. Returns null when the pitcher has no stats.
const buildStatLine = (pitcher) => {
  if (!pitcher || pitcher.era == null) return null;
  const parts = [];
  if (pitcher.wins != null && pitcher.losses != null) parts.push(`${pitcher.wins}-${pitcher.losses}`);
  if (pitcher.era != null) parts.push(`${Number(pitcher.era).toFixed(2)} ERA`);
  if (pitcher.whip != null) parts.push(`${Number(pitcher.whip).toFixed(2)} WHIP`);
  if (pitcher.innings_pitched != null) {
    const ip = Number(pitcher.innings_pitched);
    const full = Math.floor(ip);
    const frac = Math.round((ip - full) * 3);  // baseball notation: .333 → .1, .666 → .2
    parts.push(`${full}.${frac} IP`);
  }
  if (pitcher.strikeouts_pitched != null) parts.push(`${pitcher.strikeouts_pitched} K`);
  return parts.length > 0 ? parts.join(' · ') : null;
};

// YYYY-MM-DD → "TODAY — SUN, JUN 21" / "TOMORROW — …" / "SUN, JUN 21".
const formatDateHeader = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');  // noon avoids TZ rollover
  if (isNaN(d.getTime())) return dateStr;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const label = `${days[d.getDay()]}, ${months[d.getMonth()]} ${d.getDate()}`;
  if (d.toDateString() === today.toDateString()) return `Today — ${label}`;
  if (d.toDateString() === tomorrow.toDateString()) return `Tomorrow — ${label}`;
  return label;
};

// Short label for the date-nav pill ("Today" / "Jun 21").
const shortDate = (dateStr) => {
  const d = new Date(dateStr + 'T12:00:00');
  if (isNaN(d.getTime())) return dateStr;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d.getMonth()]} ${d.getDate()}`;
};

const todayISO = () => new Date().toISOString().split('T')[0];
const shiftDate = (dateStr, n) => {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
};

// ============================================================
// ProbMatch — one game (Probables v3, center option B "VS + time").
// Away pitcher left, home right; full stats under each. Ownership = colored TEXT
// label only (no filled badge). "Yours" = Option A: edge tick + name dot.
// ============================================================

// Ownership label — colored text only (8px/800), no badge block.
// YOURS = primary; owning-team = info (blue); FA = success (green).
const OwnLabel = ({ pitcher, owned }) => {
  if (!pitcher || !pitcher.mlb_id) return null;
  const base = { fontSize: 8, fontWeight: 800, whiteSpace: 'nowrap' };
  if (owned) return <span style={{ ...base, color: C.primary }}>YOURS</span>;
  if (pitcher.team_name) return <span style={{ ...base, color: C.info }}>{pitcher.team_name.substring(0, 12)}</span>;
  if (pitcher.is_free_agent !== false) return <span style={{ ...base, color: C.success }}>FA</span>;
  return null;
};

// One side of the matchup. owned → a small gold dot beside the (always-white) name.
const PitcherSide = ({ pitcher, team, owned, right, onClick }) => {
  const name = pitcher?.name || 'TBD';
  const isTBD = !pitcher?.mlb_id || name === 'TBD';
  const stats = buildStatLine(pitcher);
  const nameEl = (
    <span style={{ fontSize: 13, fontWeight: 700, color: isTBD ? C.neutral[500] : C.white, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{name}</span>
  );
  const teamEl = team ? <span style={{ fontSize: 8, color: C.neutral[500] }}>{team}</span> : null;
  // Option A name dot — only on the actually-rostered pitcher. left: dot→name; right: name→dot.
  const dot = owned && !isTBD
    ? <span aria-hidden="true" style={{ fontSize: 10, lineHeight: 1, color: C.primary, flexShrink: 0 }}>●</span>
    : null;
  return (
    <div
      data-testid="pitcher-name"
      onClick={() => !isTBD && onClick && onClick(pitcher.mlb_id)}
      role={isTBD ? undefined : 'button'}
      style={{ flex: 1, minWidth: 0, textAlign: right ? 'right' : 'left', cursor: isTBD ? 'default' : 'pointer', opacity: isTBD ? 0.55 : 1 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, justifyContent: right ? 'flex-end' : 'flex-start', minWidth: 0 }}>
        {right ? <>{teamEl}{nameEl}{dot}</> : <>{dot}{nameEl}{teamEl}</>}
      </div>
      <div><OwnLabel pitcher={pitcher} owned={owned} /></div>
      {stats && <div style={{ fontSize: 8.5, color: C.neutral[400], lineHeight: 1.5, marginTop: 3, fontVariantNumeric: 'tabular-nums' }}>{stats}</div>}
    </div>
  );
};

const ProbMatch = ({ game, isOwned, onPitcherClick }) => {
  const awayOwned = isOwned(game.away_pitcher);
  const homeOwned = isOwned(game.home_pitcher);
  const yours = awayOwned || homeOwned;
  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'stretch', gap: 8, padding: '9px 2px 9px 10px', fontVariantNumeric: 'tabular-nums' }}>
      {/* Option A edge tick — short gold bar at the left edge (NOT a full rail / tint) */}
      {yours && <span aria-hidden="true" style={{ position: 'absolute', left: 0, top: 10, bottom: 10, width: 3, borderRadius: '0 2px 2px 0', background: C.primary }} />}
      <PitcherSide pitcher={game.away_pitcher} team={game.away_team} owned={awayOwned} right={false} onClick={onPitcherClick} />
      {/* Center: VS + time (option B) */}
      <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: 44 }}>
        <span style={{ fontSize: 11, fontWeight: 900, color: C.primary }}>VS</span>
        <span style={{ fontSize: 8, color: C.neutral[500], marginTop: 1, whiteSpace: 'nowrap' }}>{game.game_time || 'TBD'}</span>
      </div>
      <PitcherSide pitcher={game.home_pitcher} team={game.home_team} owned={homeOwned} right onClick={onPitcherClick} />
    </div>
  );
};

// ============================================================
// Main page
// ============================================================

function MobileProbables() {
  // League resolution (same pattern as before).
  const [leagues, setLeagues] = useState([]);
  const { defaultLeagueId } = useDefaultLeague(leagues, true);

  useEffect(() => {
    leaguesAPI.getMyLeagues()
      .then((res) => { if (res?.success && Array.isArray(res.leagues)) setLeagues(res.leagues); })
      .catch(() => {});
  }, []);

  const leagueId = defaultLeagueId || (leagues[0]?.league_id ?? null);
  const teamId = useMemo(() => {
    if (!leagues.length || !leagueId) return null;
    return leagues.find((l) => l.league_id === leagueId)?.team_id || null;
  }, [leagues, leagueId]);

  // Single-day window (v3) — was a 5-day window.
  const [startDate, setStartDate] = useState(todayISO);
  const [data, setData] = useState({ games: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // My roster mlb_id Set → drives "yours" (Option A) + the YOURS label.
  const [rosterMlbIds, setRosterMlbIds] = useState(() => new Set());

  // Player modal
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [isPlayerModalOpen, setIsPlayerModalOpen] = useState(false);

  const loadProbables = useCallback(async () => {
    if (!leagueId || !teamId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchProbablePitchers(leagueId, teamId, startDate, 1); // single day
      if (res?.success === false) {
        setError(res.detail || 'Failed to load probables');
        setData({ games: [] });
      } else {
        setData({ games: res?.games || [] });
      }
    } catch (err) {
      setError(err?.message || 'Failed to load probables');
      setData({ games: [] });
    } finally {
      setLoading(false);
    }
  }, [leagueId, teamId, startDate]);

  useEffect(() => { loadProbables(); }, [loadProbables]);

  useEffect(() => {
    if (!leagueId || !teamId) return;
    let cancelled = false;
    leaguesAPI.getTeamRosterCanonical(leagueId, teamId)
      .then((res) => {
        if (cancelled) return;
        const players = Array.isArray(res?.players) ? res.players : [];
        // DD-PROBABLE-YOURS-FORMER-0802: build_roster_response appends FORMER players
        // (accrued-stats attribution) into the SAME players[] array, marked by an
        // `accrued_info` key that current roster players never carry. "YOURS" must mean
        // CURRENTLY rostered — without this filter a player Tony traded/dropped (Griffin
        // Jax → South of the Border, Keider Montero → Bobby Faucher, Brandon Sproat → FA)
        // is falsely flagged YOURS on the Probable page.
        setRosterMlbIds(new Set(
          players
            .filter((p) => !('accrued_info' in (p || {})))
            .map((p) => Number(p?.ids?.mlb))
            .filter(Boolean)
        ));
      })
      .catch(() => { if (!cancelled) setRosterMlbIds(new Set()); });
    return () => { cancelled = true; };
  }, [leagueId, teamId]);

  // "Yours" = on my roster (canonical mlb_id Set).
  const isOnRoster = useCallback((pitcher) => {
    const mlbId = Number(pitcher?.mlb_id);
    return Boolean(mlbId) && rosterMlbIds.has(mlbId);
  }, [rosterMlbIds]);

  const handlePitcherClick = useCallback((mlbId) => {
    if (!mlbId) return;
    setSelectedPlayerId(String(mlbId));
    setIsPlayerModalOpen(true);
  }, []);

  // A matchup is "yours" when EITHER pitcher is on your roster — the SAME isOnRoster
  // Set-membership check the Option A edge-tick/name-dot uses (no new data path).
  const isGameYours = useCallback(
    (g) => isOnRoster(g.away_pitcher) || isOnRoster(g.home_pitcher),
    [isOnRoster],
  );
  // Stable sort: your matchups to the top, everyone else beneath — each group keeps
  // its existing chronological order (Array.sort is stable; the comparator only
  // separates yours-vs-not).
  const games = useMemo(
    () => [...(data.games || [])].sort((a, b) => (isGameYours(b) ? 1 : 0) - (isGameYours(a) ? 1 : 0)),
    [data.games, isGameYours],
  );
  const isToday = startDate === todayISO();

  // Date nav ◀ [Today/date] ▶ — surface pill, primary arrows; center resets to today.
  const dateNav = (
    <span style={{ display: 'flex', alignItems: 'center', gap: 8, background: C.ground.screen, border: `1px solid ${C.border}`, borderRadius: 10, padding: '2px 8px' }}>
      <span onClick={() => setStartDate((d) => shiftDate(d, -1))} role="button" aria-label="Previous day" style={{ color: C.primary, fontSize: 12, cursor: 'pointer', lineHeight: 1 }}>◀</span>
      <span onClick={() => setStartDate(todayISO())} role="button" style={{ fontSize: 10, fontWeight: 700, color: C.white, cursor: 'pointer', whiteSpace: 'nowrap' }}>{isToday ? 'Today' : shortDate(startDate)}</span>
      <span onClick={() => setStartDate((d) => shiftDate(d, 1))} role="button" aria-label="Next day" style={{ color: C.primary, fontSize: 12, cursor: 'pointer', lineHeight: 1 }}>▶</span>
    </span>
  );

  return (
    <div style={{ padding: '4px 0 12px' }}>
      <style>{`.probables-scroll::-webkit-scrollbar { display: none; }
        @keyframes pp-spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>

      {/* Date section header (canonical 12px/600 label + 9px buffer) with the date
          nav on its right. (The team banner + "PROBABLE" badge are the parent's
          MobilePageHeader; the date nav rides this header since the page can't inject
          into the shared parent banner.) */}
      <MobileSectionHeader title={formatDateHeader(startDate)} right={dateNav} />

      {loading && (
        <div style={styles.stateBox}>
          <span style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${withAlpha(C.primary, 0.3)}`, borderTopColor: C.primary, display: 'inline-block', animation: 'pp-spin 1s linear infinite' }} />
          <div style={styles.stateText}>Loading probable pitchers…</div>
        </div>
      )}

      {!loading && error && (
        <div style={styles.stateBox}>
          <div style={{ ...styles.stateText, color: C.error }}>{error}</div>
          <button type="button" onClick={loadProbables} style={styles.retryButton}>Retry</button>
        </div>
      )}

      {!loading && !error && games.length === 0 && (
        <div style={styles.stateBox}>
          <div style={styles.stateText}>{!leagueId ? 'Set a default league' : 'No probable pitchers scheduled.'}</div>
        </div>
      )}

      {!loading && !error && games.length > 0 && (
        <div className="probables-scroll">
          {games.map((game, i) => (
            <div key={game.game_id || i} style={{ borderTop: `1.5px solid ${withAlpha(C.slate, 0.75)}` }}>
              <ProbMatch game={game} isOwned={isOnRoster} onPitcherClick={handlePitcherClick} />
            </div>
          ))}
        </div>
      )}

      <MobilePlayerModal
        playerId={selectedPlayerId}
        leagueId={leagueId}
        isOpen={isPlayerModalOpen}
        onClose={() => { setIsPlayerModalOpen(false); setSelectedPlayerId(null); }}
      />
    </div>
  );
}

const styles = {
  stateBox: { padding: '24px 16px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 },
  stateText: { fontSize: 13, color: C.neutral[200] },
  retryButton: {
    marginTop: 4, padding: '8px 16px', backgroundColor: withAlpha(C.primary, 0.1),
    color: C.primary, border: `1px solid ${C.border}`, borderRadius: 6, cursor: 'pointer', fontSize: 12,
  },
};

export default MobileProbables;
